import { createContext, useContext, useState, useEffect, useMemo, ReactNode, FunctionComponent } from 'react';

interface FlowContextValue {
    draggingType: string | null;
    setDraggingType: (type: string | null) => void;
}

const FlowContext = createContext<FlowContextValue>({
    draggingType: null,
    setDraggingType: () => {}
});

export const FlowProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
    const [draggingType, setDraggingType] = useState<string | null>(null);

    // A drag dropped outside a valid target never reaches the toolbox's onDragEnd,
    // and a stale flag here leaves every "+" inert, since AddNode ignores clicks
    // while a drag is in progress.
    useEffect(() => {
        const clear = () => setDraggingType(null);

        // Capture phase: AddNode stops propagation on drop, so a bubbling
        // listener would never see the very event that ends the drag.
        window.addEventListener('dragend', clear, true);
        window.addEventListener('drop', clear, true);

        return () => {
            window.removeEventListener('dragend', clear, true);
            window.removeEventListener('drop', clear, true);
        };
    }, []);

    const value = useMemo(() => ({ draggingType, setDraggingType }), [draggingType]);

    return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
};

export const useFlowContext = () => useContext(FlowContext);
